import type { PresenceStatus } from "@bing/shared";
import type { ReactNode } from "react";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { Avatar } from "./Avatar";
import { OfficialBadge } from "./OfficialBadge";
import { displayName, userHandle } from "./profile-ui";

// Re-exported so the many existing `from "./social-ui"` import sites stay unchanged; it lives in
// its own module because `Avatar` stamps it on the disc and importing it back here is circular.
export { OfficialBadge };

/* ------------------------------------------------------------------ *
 * Shared social-surface primitives — the felt list rows, pills, icon
 * buttons, empty/loading/error states, presence-ringed avatars and the
 * bottom sheet used by the Friends screen, the game-invite sheet and the
 * waiting-room invite panel. Extracted from `screens/Friends.tsx` so no
 * surface duplicates them.
 * ------------------------------------------------------------------ */

/* ── Inline Material-Symbol SVGs (no icon font; one `currentColor` path each). ── */
export type SocialIconName =
  | "search"
  | "personAdd"
  | "group"
  | "check"
  | "close"
  | "invite"
  | "remove"
  | "cancel"
  | "timer"
  | "flag";
const ICON_PATH: Record<SocialIconName, string> = {
  search:
    "M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0A4.5 4.5 0 1 1 14 9.5 4.5 4.5 0 0 1 9.5 14z",
  personAdd:
    "M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-1V8H4v3H1v2h3v3h2v-3h3v-2H6zm9 3c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z",
  group:
    "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
  check: "M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
  close:
    "M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
  // add_box — "invite to a table"
  invite:
    "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z",
  // flag — "report this player". The material `flag` glyph, so it reads as the same family as
  // the rest of this row's controls rather than an emoji pasted in.
  flag: "M14.4 6 14 4H5v17h2v-7h5.6l.4 2h7V6z",
  // person_remove
  remove:
    "M14 8c0-2.21-1.79-4-4-4S6 5.79 6 8s1.79 4 4 4 4-1.79 4-4zm-4 6c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm13-4h-6v2h6v-2z",
  // undo — cancel an outgoing request
  cancel:
    "M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z",
  timer:
    "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
};

export function SocialIcon({
  name,
  className,
}: {
  name: SocialIconName;
  className?: string;
}): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <path d={ICON_PATH[name]} />
    </svg>
  );
}

/** The presence-dot colour + tooltip for a friend's live status (absent = offline). */
function presenceDot(status: PresenceStatus | undefined): { className: string; title: string } {
  if (status === "online") return { className: "bg-success-win", title: fa.friends.online };
  if (status === "in_game") return { className: "bg-gold-deep", title: fa.friends.inGame };
  return { className: "bg-outline-variant", title: fa.friends.offline };
}

/** Avatar wrapped in the design's gold ring, with a live presence dot driven by
 * the presence socket (green = online, gold = in a game, muted = offline).
 * Pass no `status` on surfaces without presence to omit the dot entirely. */
export function AvatarRing({
  avatarId,
  avatarPhotoId,
  size,
  status,
  showDot = true,
}: {
  avatarId: string;
  avatarPhotoId?: string | null;
  size: number;
  status?: PresenceStatus;
  /** Render the presence dot (default true; the dot is muted when offline). */
  showDot?: boolean;
}): ReactNode {
  const dot = presenceDot(status);
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar
        avatarId={avatarId}
        avatarPhotoId={avatarPhotoId}
        size={size}
        ringClass="border-gold-deep shadow-md"
        ringWidth={1}
      />
      {showDot ? (
        <span
          aria-hidden="true"
          title={dot.title}
          className={`absolute bottom-0 left-0 h-3 w-3 rounded-full border-2 border-surface-container ${dot.className}`}
        />
      ) : null}
    </span>
  );
}

/** The identity fields every social row shows (a structural `FriendView` subset). */
export interface SocialIdentity {
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  appUsername?: string | null;
  /** Official/verified account (system + staff, e.g. «@bing»/«@Pink») → shows a badge. */
  isOfficial?: boolean | null;
}

/**
 * Official / verified badge — a gold check shown right after a system/staff identity's
 * name. One shared component so it looks identical in social rows, table seats, the chat
 * header, and the profile. Theme-aware via the gold design tokens (secondary/ink-on-gold).
 *
 * Its containers all use `flex items-center`, which centres the disc on the text LINE-BOX
 * — a hair BELOW the text's optical (cap-height) centre, so it read as sitting low. A small
 * size-proportional upward `translateY` lifts the disc to the text's optical centre so it
 * sits neatly beside the name everywhere (no layout shift; standalone renders are
 * unaffected). `nudge={false}` opts out where a badge isn't next to text.
 */

/**
 * The display name every social row shows (nickname → Telegram name → handle,
 * via the one shared resolution). The `@handle` subline renders ONLY where
 * `showHandle` is set — the Add/search tab, where users search by handle; the
 * friends/requests/invite lists show the name alone.
 */
export function Identity({
  user,
  muted = false,
  showHandle = false,
}: {
  user: SocialIdentity;
  muted?: boolean;
  showHandle?: boolean;
}): ReactNode {
  const handle = showHandle ? userHandle(user) : undefined;
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1">
        <p
          className={`m-0 truncate font-body-lg text-body-lg ${muted ? "text-on-surface-variant" : "text-on-surface"}`}
        >
          {displayName(user)}
        </p>
        {user.isOfficial ? <OfficialBadge size={15} /> : null}
      </div>
      {handle ? (
        <p className="m-0 truncate font-caption text-caption text-on-surface-variant">{handle}</p>
      ) : null}
    </div>
  );
}

/**
 * The identity HALF of a social row — the presence-ringed avatar plus the name — as ONE
 * tap target that opens that player's profile. Shared by the friends/requests/search rows
 * and «کاربران مسدود», so both screens have the same tap area and the same back behaviour
 * rather than two implementations of one pattern.
 *
 * ⚠️ `flex-1` is load-bearing. Without it the button is CONTENT-sized (flex-grow defaults
 * to 0), so `Row`'s `justify-between` leaves the space between the name and the trailing
 * actions as DEAD, untappable gap — only the avatar/name box opened the profile, which was
 * a reported bug. `min-w-0` keeps the name truncating instead of pushing the actions off-row.
 *
 * It stays a real `<button>` (never a click handler on the `Row`) so the tap target is
 * keyboard-focusable and announced — and the caller's action pills must be SIBLINGS of it,
 * since nesting a button inside a button is invalid HTML.
 *
 * Presentational on purpose: the profile is opened through `onOpen`, so this module keeps
 * its no-store rule and a test can drive it with a plain spy.
 */
/**
 * THE ONE TAP TARGET THAT OPENS SOMEBODY'S PROFILE — a bare button carrying the person's name as
 * its accessible label, and nothing else of its own.
 *
 * It exists so that a second surface wanting «tap a face, see who that is» does not write a second
 * button with its own reset classes and its own idea of what to announce. `IdentityButton` is the
 * friends-list row built on it; «برترین‌ها» wraps its own avatar and name in the same shell,
 * because a podium column needs a medal ring and a list row a muted one, and `AvatarRing`'s gold
 * is app-wide and not ours to change.
 *
 * ⚠️ The reset classes are load-bearing: Tailwind's preflight is OFF, so a `<button>` keeps the
 * UA's `2px outset` border and its own background until `border-0`/`bg-transparent` say otherwise.
 */
export function ProfileTap({
  user,
  onOpen,
  className = "",
  children,
}: {
  user: SocialIdentity;
  onOpen: () => void;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={displayName(user)}
      className={`appearance-none border-0 bg-transparent p-0 text-right cursor-pointer ${className}`}
    >
      {children}
    </button>
  );
}

export function IdentityButton({
  user,
  onOpen,
  presence,
  muted = false,
  size = 48,
  showHandle = false,
}: {
  user: SocialIdentity & { avatarId: string; avatarPhotoId?: string | null };
  onOpen: () => void;
  presence?: PresenceStatus;
  muted?: boolean;
  size?: number;
  showHandle?: boolean;
}): ReactNode {
  return (
    <ProfileTap user={user} onOpen={onOpen} className="flex min-w-0 flex-1 items-center gap-sm">
      <AvatarRing
        avatarId={user.avatarId}
        avatarPhotoId={user.avatarPhotoId}
        size={size}
        status={presence}
      />
      <Identity user={user} muted={muted} showHandle={showHandle} />
    </ProfileTap>
  );
}

/** Dashed empty/placeholder state. */
export function EmptyState({
  icon,
  text,
  hint,
}: {
  icon: SocialIconName;
  text: string;
  hint?: string;
}): ReactNode {
  return (
    <div className="flex flex-col items-center justify-center gap-sm rounded-xl border border-dashed border-outline-variant/60 bg-surface-container-low px-md py-lg text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-container text-on-surface-variant">
        <SocialIcon name={icon} className="h-6 w-6" />
      </span>
      <p className="m-0 font-body-md text-body-md text-on-surface-variant">{text}</p>
      {hint ? (
        <p className="m-0 font-caption text-caption text-on-surface-variant">{hint}</p>
      ) : null}
    </div>
  );
}

/** A spinning ring — the loading affordance. */
export function Spinner(): ReactNode {
  return (
    <div className="flex items-center justify-center gap-sm py-lg text-on-surface-variant">
      <span
        aria-hidden="true"
        className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-outline-variant"
        style={{ borderTopColor: "rgb(var(--c-secondary))" }}
      />
      <span className="font-body-md text-body-md">{fa.friends.loading}</span>
    </div>
  );
}

/** Inline list-load error with a retry. */
export function LoadError({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div className="flex flex-col items-center gap-sm rounded-xl border border-error/40 bg-error-container/20 px-md py-lg text-center">
      <p className="m-0 font-body-md text-body-md text-error">{fa.friends.loadError}</p>
      <button
        type="button"
        onClick={onRetry}
        className="cursor-pointer rounded-full border-0 bg-secondary px-4 py-1.5 font-label-md text-label-md text-ink-on-gold shadow-sm transition-transform active:scale-95"
      >
        {fa.friends.retry}
      </button>
    </div>
  );
}

/** A felt list row (avatar + identity + trailing actions). */
export function Row({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="flex items-center justify-between gap-sm rounded-xl border border-surface-container-highest bg-surface-container p-sm shadow-sm">
      {children}
    </div>
  );
}

/** A round icon action button. */
export function IconButton({
  icon,
  label,
  onClick,
  disabled,
  tone,
}: {
  icon: SocialIconName;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone: "accept" | "reject";
}): ReactNode {
  const tones = {
    accept: "bg-primary-container text-on-primary-container hover:bg-primary",
    reject: "bg-surface-variant text-on-surface-variant hover:bg-surface-bright",
  } as const;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={`flex h-10 w-10 cursor-pointer appearance-none items-center justify-center rounded-full border-0 shadow-sm transition-colors active:scale-95 disabled:opacity-50 disabled:active:scale-100 ${tones[tone]}`}
    >
      <SocialIcon name={icon} className="h-5 w-5" />
    </button>
  );
}

/** A text pill action (gold = primary, outline = secondary, muted = disabled). */
export function Pill({
  label,
  icon,
  onClick,
  disabled,
  variant,
}: {
  label: string;
  icon?: SocialIconName;
  onClick?: () => void;
  disabled?: boolean;
  variant: "primary" | "outline" | "muted";
}): ReactNode {
  const variants = {
    primary: "border-0 bg-secondary text-ink-on-gold hover:bg-secondary-fixed shadow-sm",
    outline:
      "border border-outline-variant bg-surface-container text-secondary hover:bg-surface-bright",
    muted: "border border-outline-variant/50 bg-surface-variant text-on-surface-variant",
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || variant === "muted"}
      className={`flex shrink-0 cursor-pointer appearance-none items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-bold leading-none transition-colors active:scale-95 disabled:cursor-default disabled:active:scale-100 ${variants[variant]}`}
    >
      {icon ? <SocialIcon name={icon} className="h-3.5 w-3.5" /> : null}
      <span>{label}</span>
    </button>
  );
}

/** Accent section header (icon + title) with an optional trailing count. */
export function SectionHeader({
  icon,
  title,
  count,
}: {
  icon: SocialIconName;
  title: string;
  count?: number;
}): ReactNode {
  return (
    <div className="flex items-center justify-between border-b border-outline-variant pb-xs">
      <h2 className="m-0 flex items-center gap-xs font-headline-sm text-headline-sm text-tertiary">
        <SocialIcon name={icon} className="h-5 w-5" />
        {title}
      </h2>
      {count !== undefined ? (
        <span
          className="font-label-md text-label-md text-on-surface-variant"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {faDigits(count)} {fa.friends.count}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The app's bottom sheet (used by the Lobby chooser, the social sheets and the
 * in-game table sheet): dimmed backdrop that dismisses on tap, a rounded-top
 * surface with the grab handle, safe-area padded. Opens with the app's shared
 * entrance — backdrop fade-in + panel slide-up (the `bg-fadein`/`bg-slidein`
 * keyframes from styles/theme.css). `flush` drops the panel's own content and
 * safe-area padding (keeping just the grab handle) for children that manage
 * their own edges — e.g. the table-chat composer, which already pads the
 * safe-area bottom itself (padding it twice would stack two insets).
 */
export function Sheet({
  label,
  onClose,
  children,
  flush = false,
  header,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
  flush?: boolean;
  /** PINNED between the handle and the scroller — a way out rendered as a child scrolls away on
   *  a short phone at exactly the moment the reader is at the bottom of a long sheet. */
  header?: ReactNode;
}): ReactNode {
  return (
    <div
      role="dialog"
      aria-label={label}
      dir="rtl"
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60"
      style={{ animation: "bg-fadein 0.2s ease-out" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`flex w-full max-w-container-max flex-col rounded-t-xl border-t border-gold-deep/20 bg-surface-container shadow-[0_-8px_40px_rgba(0,0,0,0.5)] ${
          flush ? "pt-3" : "pt-md"
        }`}
        style={{
          animation: "bg-slidein 0.25s ease-out",
          // ⚠️ THE CAP IS LOAD-BEARING — WITHOUT IT A TALL SHEET IS A TRAP, NOT A LAYOUT WART.
          //
          // The panel is BOTTOM-anchored (`items-end`), so content taller than the viewport grows
          // UPWARD and its top — the title and the «بازگشت» button — goes off-screen at a NEGATIVE
          // y. Measured on a 320×568 viewport before this: the حکم mode chooser rendered 688px in a
          // 568px viewport, back button at −84, `scrollHeight === clientHeight` (nothing scrolled),
          // and `backdropVisiblePx: 0` — so the backdrop-tap escape below was unreachable too. Two
          // ways out of a sheet, both gone, on the game that is ~90% of platform usage.
          //
          // Bounded against `--app-h` (= min(Telegram's stable height, 100dvh)) rather than the
          // fixed container's own `100%`: on a real phone the layout viewport can be TALLER than
          // the visible area, and the smaller of the two is the honest number. `min(100%, …)` keeps
          // it correct if `--app-h` is ever unset. The top gap leaves a real, tappable strip of
          // backdrop AND clears the device notch / Telegram chrome.
          maxHeight:
            "calc(min(100%, var(--app-h, 100%)) - 48px - max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
        }}
      >
        <span className="mx-auto mb-4 block h-1 w-12 flex-none rounded-full bg-surface-variant" />
        {header ? <div className={`flex-none ${flush ? "" : "px-md"}`}>{header}</div> : null}
        {/* The handle stays pinned; only the CONTENT scrolls. `min-h-0` + a non-visible overflow
            are what let this flex item shrink past its content once the cap bites. The bottom
            safe-area padding lives in HERE so the last row clears the home indicator at the end of
            a scroll — `flush` owns its own insets (its composer pads itself). The HORIZONTAL
            padding moved in here too: a scroll container clips to its own padding box, and the
            buttons inside carry a `shadow-lg` that the panel's edge would otherwise shave. */}
        <div
          className={`min-h-0 overflow-y-auto overscroll-contain ${flush ? "" : "px-md"}`}
          style={
            flush
              ? undefined
              : {
                  paddingBottom:
                    "calc(16px + max(env(safe-area-inset-bottom), var(--tg-safe-bottom, 0px)))",
                }
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}
